import { useEffect, useState, useMemo } from 'react';
import { MenuCategory, MenuItem } from '@lunit/shared-types';
import { getPublicMenu } from '@core/api/menu';
import { useVenue } from '@core/contexts/VenueContext';
import { Card } from '@core/components/Card';
import { Skeleton } from '@core/components/Skeleton';
import { Star, UtensilsCrossed } from 'lucide-react';
import { Header } from '@core/components/Header';

export function MenuPage() {
  const { venue } = useVenue();
  const [categories, setCategories] = useState<MenuCategory[]>([]);
  const [allItems, setAllItems] = useState<MenuItem[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (venue) {
      void loadMenu();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venue?.id]);

  const loadMenu = async () => {
    try {
      if (!venue) return;
      setLoading(true);
      setError(null);
      const data = await getPublicMenu(venue.id);
      setCategories(data.categories);
      setAllItems(data.items);
    } catch (err: any) {
      console.error('Failed to load menu:', err);
      if (err?.message?.includes('MENU_NOT_PUBLISHED')) {
        setError('Меню не опубликовано');
      } else {
        setError('Не удалось загрузить меню');
      }
    } finally {
      setLoading(false);
    }
  };

  // Filter items based on selected category
  const items = useMemo(() => {
    return allItems.filter((item) => {
      if (selectedCategory && item.categoryId !== selectedCategory) {
        return false;
      }
      return true;
    });
  }, [allItems, selectedCategory]);

  if (loading) {
    return (
      <div className="p-4 pt-[96px] space-y-4">
        <Skeleton className="h-12" />
        <div className="flex gap-2">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-10 w-24" />
          ))}
        </div>
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-32" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col bg-page">
        <div className="p-4 pt-[96px]">
          <Header />
        </div>
        <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
          <UtensilsCrossed className="w-16 h-16 text-text-main opacity-30 mb-4" />
          <p className="text-text-main opacity-60">{error}</p>
        </div>
      </div>
    );
  }

  const featuredItems = items.filter((item) => item.isFeatured);

  return (
    <div className="min-h-screen bg-page pb-20">
      {/* Fixed header + tabs container (sticky doesn't work with position:fixed on body) */}
      {/* top-[80px] accounts for Telegram fullscreen safe area */}
      <div className="fixed top-[80px] left-0 right-0 z-50 bg-page">
        {/* Custom header without sticky (replaces Header component) */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <button
            onClick={() => window.history.back()}
            className="p-2 -ml-2 rounded-full hover:bg-secondary transition-colors"
            aria-label="Назад"
          >
            <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-lg font-bold text-text-main">{venue?.name || 'Меню'}</h1>
          <div className="w-10" />
        </div>

        {/* Category tabs */}
        <div className="flex gap-2 overflow-x-auto px-4 py-3 scrollbar-hide border-b border-border">
          <button
            onClick={() => setSelectedCategory(null)}
            className={`px-4 py-2 rounded-brand whitespace-nowrap transition-all ${
              selectedCategory === null
                ? 'bg-primary text-primary-foreground'
                : 'bg-secondary text-text-on-secondary'
            }`}
          >
            Все
          </button>
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`px-4 py-2 rounded-brand whitespace-nowrap transition-all ${
                selectedCategory === category.id
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-secondary text-text-on-secondary'
              }`}
            >
              {category.icon} {category.name}
            </button>
          ))}
        </div>
      </div>

      {/* Menu items list - pt-[184px] = 80px safe area + 104px header */}
      <div className="pt-[184px] p-4 space-y-6">
        {!selectedCategory && featuredItems.length > 0 && (
          <div>
            <h2 className="text-lg font-semibold mb-3 text-text-main flex items-center gap-2">
              <Star className="w-5 h-5 text-primary" />
              Рекомендуем
            </h2>
            <div className="space-y-3">
              {featuredItems.map((item) => (
                <MenuItemCard key={item.id} item={item} />
              ))}
            </div>
          </div>
        )}

        <div>
          {selectedCategory && (
            <h2 className="text-lg font-semibold mb-3 text-text-main">
              {categories.find((category) => category.id === selectedCategory)?.name}
            </h2>
          )}
          {!selectedCategory && !featuredItems.length && (
            <h2 className="text-lg font-semibold mb-3 text-text-main">Все позиции</h2>
          )}

          {items.length === 0 ? (
            <Card className="text-center py-8 text-text-main opacity-60">
              Ничего не найдено
            </Card>
          ) : (
            <div className="space-y-3">
              {items
                .filter((item) => !item.isFeatured || selectedCategory)
                .map((item) => (
                  <MenuItemCard key={item.id} item={item} />
                ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function MenuItemCard({ item }: { item: MenuItem }) {
  return (
    <Card>
      <div className="flex gap-4">
        {item.imageUrl && (
          <img
            src={item.imageUrl}
            alt={item.name}
            className="w-24 h-24 rounded-brand object-cover flex-shrink-0"
          />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-1">
            <h3 className="font-semibold text-text-main line-clamp-1">{item.name}</h3>
            {item.isFeatured && (
              <Star className="w-4 h-4 text-primary flex-shrink-0" />
            )}
          </div>
          {item.description && (
            <p className="text-sm text-text-muted line-clamp-2 mb-2">
              {item.description}
            </p>
          )}
          <div className="flex items-center justify-between">
            <div className="font-bold text-lg text-primary">₽{Math.round(item.price)}</div>
            {item.calories && (
              <span className="text-xs text-text-muted">{item.calories} ккал</span>
            )}
          </div>
          {item.allergens && item.allergens.length > 0 && (
            <div className="mt-2 text-xs text-text-muted">
              Аллергены: {item.allergens.join(', ')}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
